/* eslint-disable react-hooks/exhaustive-deps */
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  createContext
} from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

import { useGlobalContext } from 'context/GlobalContext';

import { GenerateXLSX } from './xlsx';
import {
  FormatFile,
  MAXIMUN_ROWS_IN_PDF,
  MESSAGE_ERROR_PDF,
  optionsFormatExport
} from './utils';
import { IconExport } from './IconExport';
import ExportPDF from '../PDFExport';
import { PDFtoBase64 } from './PDFBase64';
import { DropDownSelectButton } from '../DropDownSelectButton';

export type TFormat = 'PDF' | 'CSV' | 'XLSX';
export type TOption = {
  id: number;
  label: string;
  type: TFormat;
  title?: string;
  color: string;
  fileName?: string;
};

export type TypeButtons = (typeof optionsFormatExport)[number]['type'][];

interface Props {
  fileName: string;
  filterButton?: TypeButtons;
  handleUploadFile?: (data: {
    fileContent: unknown;
    fileName: string;
    description: string;
  }) => void;
  headers: { key: string; label: string }[];
  isLandscapePDF?: boolean;
  isSendingEmail?: boolean;
  items: { [key: string]: unknown }[];
  maximRowToExportPDF?: number;
  messageErrorPDF?: string;
  onExport: (selectedOption?: TOption) => void;
  typeFormatExport?: (type: string) => string;
  limitCSVandXLSX?: boolean;
  sendEmailBase64?: boolean;
  totalCount?: number;
  setExportOption?: (option: TFormat) => void;
}

/**
 * The `ExportOptions` component is a React component that renders a grid of export
  options buttons. It takes in several props including:
  @param items prop is an `array` that contains the data that will be exported. It is used in the
  `ExportOptions` component to generate the export file in the selected format (PDF, CSV, or XLSX).
  @param  headers prop is an `array` that contains the keys and labels for the columns in the exported
  file. It is used in the `ExportOptions` component to generate the headers for the exported file.
  @param fileName prop is a `string` that specifies the name of the exported file.
  @param totalCount prop is a used to determine the total number of items in the data that will be
  exported. It is used in the `ExportOptions` component to check if the selected export option is
  PDF and if the total number of items exceeds the maximum number of rows allowed in a PDF file. If
  the total count exceeds the maximum, an error message is shown.
  @param  isLandscapePDF prop is used to determine whether the exported PDF file should be in
  landscape orientation or not. By default, the PDF file is generated in portrait orientation. If
  `isLandscapePDF` is set to `true`, the PDF file will be generated in landscape orientation.
  @param onExport function is a callback function that is called when the export functionality is
  triggered. It is typically used to perform any additional actions or updates that need to be done
  after the export is completed, such as showing a success message or updating the UI.
  @param sendEmailBase64 prop is a `boolean` value that determines whether the exported file should be
  sent as a base64 encoded string in an email. If `sendEmailBase64` is set to `true`, the exported
  file will be converted to a base64 encoded string and sent in an email. This can be useful when
  the exported file needs to be attached to an email and sent programmatically.
  @param handleUploadFile function is a callback function that is passed as a prop to the
  `ExportOptions` component. It is used to handle the upload of a file after it has been exported.
  The function takes in an object parameter with the following properties:
  `fileContent`,
  `fileName`,
  `description`
  @param maximRowToExportPDF prop is a `number` value that specifies the maximum number of items
  allowed in a PDF file. If the total number of items in the data exceeds the maximum, an error
  message is shown.
  @param messageErrorPDF prop is a `string` that specifies the error message to be shown if the
  total number of iteems in the data xceeds the maximum number of items allowed in a PDF file.
  @param setExportOption prop is a function that is used to set the export option in the parent
  component. It is typically used to update the state or context of the parent component with the
  selected export option.

*/

export function ExportOptions({
  fileName,
  filterButton,
  handleUploadFile,
  headers,
  isLandscapePDF = false,
  isSendingEmail = false,
  items,
  maximRowToExportPDF,
  messageErrorPDF,
  onExport,
  typeFormatExport,
  sendEmailBase64 = false,
  limitCSVandXLSX = false,
  totalCount,
  setExportOption = null
}: Props) {
  // Este selectOption es para que no se quede seleccionado el botón de exportar si llega a fallar la api
  // Se decidio manejarlo desde el global context ya que estaba impactando en todos los catalogos
  const { showError, selectedOption, setSelectedOption } = useGlobalContext();

  const [isDesktop, setIsDesktop] = useState<boolean>(window.innerWidth > 880);
  const { t } = useTranslation('global');

  const mobileMediaQuery = window.matchMedia('(max-width: 880px)');
  const desktopMediaQuery = window.matchMedia('(min-width: 881px)');

  mobileMediaQuery.onchange = (event) => {
    if (event.matches) setIsDesktop(false);
  };
  desktopMediaQuery.onchange = (event) => {
    if (event.matches) setIsDesktop(true);
  };

  const handleClickSelection = (option: TOption) => {
    if (totalCount === 0) {
      showError(t('errorMessages.noData'));
      return;
    }
    setSelectedOption(option);
    if (setExportOption) setExportOption(option?.type);
  };

  const getXLSX = useCallback(
    (format: 'xlsx' | 'csv') => {
      if (limitCSVandXLSX && totalCount > MAXIMUN_ROWS_IN_PDF) {
        return;
      }

      if (limitCSVandXLSX && totalCount > maximRowToExportPDF) {
        return;
      }

      GenerateXLSX({
        items,
        headers,
        fileName,
        format,
        sendEmailBase64,
        handleUploadFile
      });
      handleClickSelection(undefined);
    },
    [fileName, handleUploadFile, headers, items, sendEmailBase64]
  );

  const getPDF = () => {
    if (totalCount > MAXIMUN_ROWS_IN_PDF) {
      return;
    }
    // SE DUPLICA EL CODIGO PARA PODER VALIDAR EL BUG SDMSE-28139 Y NO ROMPER LO QUE EXISTE
    if (totalCount > maximRowToExportPDF) {
      return;
    }
    if (sendEmailBase64) {
      PDFtoBase64({
        items,
        headers,
        fileName,
        isLandscape: isLandscapePDF,
        handleUploadFile
      });
    } else {
      ExportPDF({
        data: items,
        dataHeaders: headers,
        fileName,
        isLandscape: isLandscapePDF
      });
    }
    handleClickSelection(undefined);
  };

  const exportFileByFormatDictionary = {
    PDF: () => getPDF(),
    XLSX: () => getXLSX('xlsx'),
    CSV: () => getXLSX('csv')
  };

  useEffect(() => {
    if (
      selectedOption &&
      selectedOption.fileName &&
      selectedOption.fileName === fileName
    ) {
      if (
        (selectedOption.type === FormatFile.PDF ||
          (limitCSVandXLSX && selectedOption.type === FormatFile.CSV) ||
          (limitCSVandXLSX && selectedOption.type === FormatFile.XLSX)) &&
        ((totalCount > MAXIMUN_ROWS_IN_PDF && !items?.length) ||
          (items?.length > 0 && items?.length > MAXIMUN_ROWS_IN_PDF))
      ) {
        showError(t(MESSAGE_ERROR_PDF));
        setSelectedOption(null);
        return;
      }
      // SE DUPLICA EL CODIGO PARA PODER VALIDAR EL BUG SDMSE-28139 Y NO ROMPER LO QUE EXISTE
      if (
        (selectedOption.type === FormatFile.PDF ||
          (limitCSVandXLSX && selectedOption.type === FormatFile.CSV) ||
          (limitCSVandXLSX && selectedOption.type === FormatFile.XLSX)) &&
        ((totalCount > maximRowToExportPDF && !items?.length) ||
          (items?.length > 0 && items?.length > maximRowToExportPDF))
      ) {
        showError(messageErrorPDF);
        setSelectedOption(null);
        return;
      }
      onExport(selectedOption);
      // Verificar si typeFormatExport es una función antes de ejecutarla
      if (typeof typeFormatExport === 'function') {
        typeFormatExport(selectedOption?.type);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    if (
      items?.length &&
      selectedOption &&
      selectedOption.fileName &&
      selectedOption.fileName === fileName
    ) {
      exportFileByFormatDictionary[selectedOption.type]();
    }
  }, [items, selectedOption]);

  const buttons = useMemo(() => {
    if (!filterButton) return optionsFormatExport;

    return optionsFormatExport.filter((option) =>
      filterButton.includes(option.type)
    );
  }, [filterButton]);

  return (
    <Grid justifyContent="flex-end" item>
      {isDesktop ? (
        buttons.map((option) => (
          <Button
            aria-label={option.label}
            disabled={isSendingEmail}
            key={option.id}
            onClick={() => {
              handleClickSelection({ ...option, fileName });
            }}
            size="small"
            startIcon={
              <IconExport fill={isSendingEmail ? '#A0A0A0' : option.color} />
            }
            sx={{ color: option.color, marginRight: '.3rem' }}
          >
            {option.title}
          </Button>
        ))
      ) : (
        <DropDownSelectButton
          disabled={isSendingEmail}
          options={buttons.map((button) => ({
            ...button,
            fileName
          }))}
          selectedOption={selectedOption?.id}
          setSelectedOption={handleClickSelection}
          size="small"
          title="Exportar"
        />
      )}
    </Grid>
  );
}
